/**
 * Schemas de validação na fronteira (doc 13.6): todo dado externo (HTTP, sync,
 * formulário) é validado aqui antes de virar entidade de domínio.
 */

import { z } from "zod";
import type { SupervisaoData } from "./apoio.ts";
import { LIMITE_BYTES_EVIDENCIA } from "./midia.ts";

export const nivelMaturidadeSchema = z.enum(["0", "1", "2", "3", "4", "na"]);
export const tipoConstatacaoSchema = z.enum(["documento", "observacao", "declaracao", "ausencia"]);
export const momentoSchema = z.enum(["mi", "mf"]);
export const papelSchema = z.enum([
  "gqa",
  "ggo",
  "ncig",
  "audin",
  "coord",
  "presidente",
  "admin",
  "visualizador",
  "unidade",
]);
export const eixoUsuarioSchema = z.enum(["IMGO", "IMQA", "IMGR"]).nullable();
/** Cargo da conta `unidade` no Portal do Plano de Ação (identidade + escopo). */
export const cargoUnidadeSchema = z.enum(["diretor", "coordenador", "gerente", "supervisor"]);

/**
 * Tamanho máximo do login (= e-mail). Era 40 e barrava e-mails institucionais
 * longos ("nome.do.meio.sobrenome@emserh.ma.gov.br") na criação E no login.
 */
export const MAX_MATRICULA = 120;

export const loginSchema = z.object({
  matricula: z.string().min(3).max(MAX_MATRICULA),
  senha: z.string().min(6).max(200),
});
export type LoginDTO = z.infer<typeof loginSchema>;

/* ---------------- Escopo do ciclo (setores existentes na unidade) ---------------- */

/** Código de setor (05A..05R, com sufixo opcional p/ desdobrados: 05E2/05E3). */
const setorCodigoSchema = z.string().regex(/^05[A-R][0-9]?$/);

/** Uma (de)seleção de setor na trilha de auditoria do escopo. */
const escopoLogSchema = z.object({
  setor: setorCodigoSchema,
  ativo: z.boolean(),
  por: z.string().max(120),
  em: z.string().datetime(),
});

/** Escopo do ciclo (setores existentes na unidade) — validado na borda (doc 17b §4). */
export const escopoDataSchema = z.object({
  setoresAtivos: z.array(setorCodigoSchema).max(30),
  escopoConfirmado: z.boolean(),
  escopoConfirmadoPor: z.string().max(120).optional(),
  escopoConfirmadoEm: z.string().datetime().optional(),
  escopoLog: z.array(escopoLogSchema).max(500).default([]),
  amostraLiberada: z.boolean().optional(),
});
export type EscopoDataDTO = z.infer<typeof escopoDataSchema>;

export const respostaItemSchema = z.object({
  itemId: z.string().min(1).max(20),
  mi: nivelMaturidadeSchema.nullable().optional(),
  mf: nivelMaturidadeSchema.nullable().optional(),
  p: z.number().int().min(1).max(5).optional(),
  i: z.number().int().min(1).max(5).optional(),
  evidenciaMi: z.string().max(2000).optional(),
  evidenciaMf: z.string().max(2000).optional(),
  tipoMi: tipoConstatacaoSchema.nullable().optional(),
  tipoMf: tipoConstatacaoSchema.nullable().optional(),
  fonteMi: z.string().max(200).optional(),
  fonteMf: z.string().max(200).optional(),
  autor: z.string().max(120).optional(),
  ts: z.string().datetime().optional(),
});
export type RespostaItemDTO = z.infer<typeof respostaItemSchema>;

/** Uma operação idempotente do push em lote (chave única por operação). */
export const operacaoSyncSchema = z.object({
  operacaoId: z.string().uuid(),
  avaliacaoId: z.string().min(1).max(60),
  respostas: z.array(respostaItemSchema).max(500),
  qaciFinal: z.number().min(-1).max(1).optional(),
  ims: z.number().min(0).max(1).optional(),
  /** Escopo do ciclo (setores ativos) — sincroniza junto com as respostas (doc 17b §6). */
  escopo: escopoDataSchema.optional(),
  atualizadoEm: z.string().datetime(),
});
export type OperacaoSyncDTO = z.infer<typeof operacaoSyncSchema>;

export const pushSchema = z.object({
  operacoes: z.array(operacaoSyncSchema).min(1).max(50),
});
export type PushDTO = z.infer<typeof pushSchema>;

export const pullQuerySchema = z.object({
  desde: z.string().datetime().optional(),
});
export type PullQueryDTO = z.infer<typeof pullQuerySchema>;

/* ---------------- Supervisão AUDIN (push do blob) ---------------- */

const supResultadoSchema = z.enum(["C", "NC", "NA"]).nullable();
const supCriterioSchema = z.object({
  mat: nivelMaturidadeSchema.nullable().optional(),
  resultado: supResultadoSchema.optional(),
  likert: z.number().int().min(1).max(5).nullable().optional(),
  registro: z.string().max(2000).optional(),
  acao: z.enum(["S", "N"]).nullable().optional(),
});
const supAmostraSchema = z
  .object({
    itemId: z.string().min(1).max(20),
    estrato: z.enum(["CORE", "RiscoInerente", "Aleatorio", "Divergencia"]),
    evidencia: z.string().max(2000).optional(),
    matSup: nivelMaturidadeSchema.nullable().optional(),
    obs: z.string().max(2000).optional(),
    status: z.enum(["Validar", "Revisar"]).nullable().optional(),
    // Compat retroativa: aceita o nome/valor antigos (encaminhamento / Manter).
    encaminhamento: z.enum(["Manter", "Revisar"]).nullable().optional(),
  })
  .transform(({ encaminhamento, status, ...resto }) => ({
    ...resto,
    status: status ?? (encaminhamento === "Manter" ? "Validar" : (encaminhamento ?? null)),
  }));
const supContestacaoSchema = z.object({
  id: z.string().min(1).max(60),
  item: z.string().max(20),
  nc: z.string().max(2000).optional(),
  original: supResultadoSchema.optional(),
  argumento: z.string().max(2000).optional(),
  analise: z.string().max(2000).optional(),
  modifica: z.enum(["S", "N"]).nullable().optional(),
  decisao: z.string().max(200).optional(),
  justificativa: z.string().max(2000).optional(),
  norma: z.string().max(400).optional(),
});
/** Resolução de uma divergência (amostra × checklist): qual nota prevalece. */
const supResolucaoSchema = z.object({
  escolha: z.enum(["avaliador", "supervisor"]),
  justificativa: z.string().max(2000).optional(),
});
export const supervisaoDadosSchema = z.object({
  criterios: z.record(z.string().max(20), supCriterioSchema),
  amostra: z.array(supAmostraSchema).max(300),
  contestacoes: z.array(supContestacaoSchema).max(50),
  /**
   * Decisão do supervisor por item divergente (chave = itemId). `default({})` e
   * não `optional()`: a chave passa a existir SEMPRE na saída do parse, então
   * não pode mais sumir por acidente na leitura (doc 20 §I4).
   */
  resolucoes: z.record(z.string().max(20), supResolucaoSchema).default({}),
  parecer: z.enum(["Confiavel", "Ressalvas", "NaoConfiavel"]).nullable(),
  assinatura: z.string().max(200).optional(),
  /** Revisão do sorteio da amostra cega (cada "refazer" incrementa). */
  amostraRev: z.number().int().min(0).optional(),
});

/**
 * Trava de deriva entre o TIPO e o SCHEMA da supervisão (doc 20 §I4).
 *
 * `SupervisaoData` (apoio.ts) e `supervisaoDadosSchema` são duas declarações
 * escritas à mão sobre o MESMO contrato — e derivaram: `resolucoes` existia no
 * tipo, não subia no payload e era descartada em silêncio pelo zod (a chave não
 * declarada é removida, sem erro). Comparar só a assinabilidade não pega isso,
 * porque tipagem estrutural aceita propriedade a mais. Comparar os CONJUNTOS DE
 * CHAVES pega: adicionar campo num lado sem o outro quebra o `bun run typecheck`.
 *
 * Puramente em tipo — não emite runtime.
 */
type MesmasChaves<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Afirma<T extends true> = T;
export type _ChavesDaSupervisaoBatem = Afirma<
  MesmasChaves<keyof SupervisaoData, keyof z.input<typeof supervisaoDadosSchema>>
>;
/**
 * Serializa a supervisão para o push, DERIVANDO do objeto inteiro (doc 20 §I4).
 *
 * Mora aqui, e não junto do tipo, porque é a contraparte de ESCRITA do mesmo
 * contrato que `supervisaoDadosSchema` valida na leitura — e por isso é tipada
 * pela SAÍDA do schema — o que a borda de fato aceita (arrays mutáveis e `status`
 * já normalizado, ao contrário do tipo de domínio).
 *
 * Existe porque o chamador montava o payload campo a campo e esquecia
 * `resolucoes`: como o schema a tratava como opcional, o zod removia a chave sem
 * erro e cada autosave apagava a arbitragem de divergência no servidor — a mesma
 * que alimenta os overrides do IMGQ. Ninguém percebia.
 *
 * A regra passa a ser: o payload é o objeto inteiro, e aqui ficam só as
 * normalizações que a BORDA exige. Campo novo em `SupervisaoData` sobe sozinho, e
 * a trava de chaves acima garante que o schema acompanhe.
 */
export function paraPushDados(d: SupervisaoData): z.infer<typeof supervisaoDadosSchema> {
  const { assinatura, criterios, ...resto } = d;
  return {
    ...resto,
    criterios: { ...criterios },
    // `status` é obrigatório na saída do schema (o `.transform` de compat migra
    // `encaminhamento`); normalizar aqui evita depender do default do zod.
    amostra: d.amostra.map((a) => ({ ...a, status: a.status ?? null })),
    contestacoes: [...d.contestacoes],
    resolucoes: { ...(d.resolucoes ?? {}) },
    amostraRev: d.amostraRev ?? 0,
    // Assinatura vazia é ausência de assinatura — não ocupa espaço no blob.
    ...(assinatura ? { assinatura } : {}),
  };
}

export const supervisaoPushSchema = z.object({
  avaliacaoId: z.string().min(1).max(60),
  dados: supervisaoDadosSchema,
  validar: z.boolean().optional(),
  /**
   * Versão do blob sobre a qual este cliente editou (concorrência otimista,
   * doc 20 §I6). Ausente = cliente antigo, que ainda não a conhece: o servidor
   * dispensa a guarda, senão o rollout quebraria todo PWA não atualizado.
   */
  versaoBase: z.number().int().min(0).optional(),
  atualizadoEm: z.string().datetime(),
});
export type SupervisaoPushDTO = z.infer<typeof supervisaoPushSchema>;

/* ---------------- Formulário público QACI/IMGR (sem auth) ---------------- */

export const imgrRespostaSchema = z.object({
  cargo: z.string().min(1).max(120),
  nivel: z.enum(["Estratégico", "Tático", "Operacional"]),
  area: z.string().min(1).max(80),
  perfil: z.enum(["Gestor", "Liderado"]),
  setor: z.string().max(80).optional(),
  // num(1..31) → nota(0..3)
  respostas: z.record(z.string().max(3), z.number().int().min(0).max(3)),
});
export type ImgrRespostaDTO = z.infer<typeof imgrRespostaSchema>;

/* ---------------- Gestão de usuários (admin) ---------------- */

/** Login (= e-mail): sem espaços — um espaço colado junto vira login que ninguém consegue digitar. */
const matriculaSchema = z
  .string()
  .trim()
  .min(3)
  .max(MAX_MATRICULA)
  .regex(/^\S+$/, "O e-mail de login não pode ter espaços");
const senhaSchema = z.string().min(8).max(200);

/** Id da unidade (número oficial da lista EMSERH, como string). */
const unidadeIdSchema = z.string().min(1).max(60);

/** Setores (05A..05R) que uma conta `unidade` enxerga; vazio ⇒ diretor (tudo). */
const setoresUsuarioSchema = z.array(setorCodigoSchema).max(30);

export const criarUsuarioSchema = z
  .object({
    nome: z.string().min(2).max(120),
    matricula: matriculaSchema,
    senha: senhaSchema,
    papel: papelSchema,
    eixo: eixoUsuarioSchema.optional(),
    /** Vínculo obrigatório quando `papel === "unidade"`; ignorado nos demais. */
    unidadeId: unidadeIdSchema.nullable().optional(),
    /** Cargo na unidade (obrigatório p/ `unidade`); ignorado nos demais. */
    cargoUnidade: cargoUnidadeSchema.nullable().optional(),
    /** Legado: contas de unidade agora veem a unidade inteira (cargo = rótulo); ignorado. */
    setores: setoresUsuarioSchema.optional(),
  })
  .refine((o) => o.papel !== "unidade" || !!o.unidadeId, {
    message: "Selecione a unidade para o papel Unidade",
    path: ["unidadeId"],
  })
  .refine((o) => o.papel !== "unidade" || !!o.cargoUnidade, {
    message: "Selecione o cargo na unidade",
    path: ["cargoUnidade"],
  });
export type CriarUsuarioDTO = z.infer<typeof criarUsuarioSchema>;

export const atualizarUsuarioSchema = z
  .object({
    nome: z.string().min(2).max(120).optional(),
    /** Novo e-mail de login. O servidor canoniza (minúsculas) e recusa duplicado. */
    matricula: matriculaSchema.optional(),
    papel: papelSchema.optional(),
    eixo: eixoUsuarioSchema.optional(),
    ativo: z.boolean().optional(),
    unidadeId: unidadeIdSchema.nullable().optional(),
    cargoUnidade: cargoUnidadeSchema.nullable().optional(),
    setores: setoresUsuarioSchema.optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: "Nada para atualizar" });
export type AtualizarUsuarioDTO = z.infer<typeof atualizarUsuarioSchema>;

export const redefinirSenhaSchema = z.object({ senha: senhaSchema });
export type RedefinirSenhaDTO = z.infer<typeof redefinirSenhaSchema>;

/** Auto-serviço: qualquer usuário troca a própria senha confirmando a atual. */
export const trocarSenhaSchema = z.object({
  senhaAtual: z.string().min(1).max(200),
  novaSenha: senhaSchema,
});
export type TrocarSenhaDTO = z.infer<typeof trocarSenhaSchema>;

/** Usuário como devolvido pela API (nunca expõe o hash de senha). */
export interface UsuarioPublico {
  id: string;
  nome: string;
  matricula: string;
  papel: string;
  eixo: string | null;
  ativo: boolean;
  /** Unidade vinculada (só para o papel `unidade`); `null` nos demais. */
  unidadeId: string | null;
  /** Cargo na unidade (só `unidade`): diretor|coordenador|gestor|supervisor. */
  cargoUnidade: string | null;
  /** Setores (05A..05R) que a conta enxerga; `null`/vazio ⇒ diretor (tudo). */
  setores: string[] | null;
  /**
   * `true` enquanto existe senha provisória vigente — a pessoa ainda NÃO entrou
   * desde que a conta foi criada/teve a senha redefinida. Opcional: uma API
   * antiga (PWA novo × servidor ainda não atualizado) simplesmente não manda.
   */
  acessoPendente?: boolean;
  /** Data de criação da conta (ISO 8601). Opcional pelo mesmo motivo. */
  criadoEm?: string;
}

/* ---------------- Plano de Ação (metodologia FACR) ---------------- */

const statusPlanoItemSchema = z.enum(["pendente", "em_andamento", "rejeitado", "concluido"]);

/** Prazo máximo de uma ação do plano: 3 meses a partir de hoje (regra da CAINT). */
export const MAX_MESES_PRAZO = 3;

/** Data máxima permitida para um prazo: `base` + 3 meses (calendário). */
export function prazoMaximo(base: Date = new Date()): Date {
  const d = new Date(base);
  d.setMonth(d.getMonth() + MAX_MESES_PRAZO);
  return d;
}

/**
 * true se o prazo (ISO) está dentro do limite: de hoje até hoje+3 meses (ou vazio).
 * Compara instantes com folga de ~1 dia para absorver diferença de fuso entre o
 * cliente (grava o prazo ao meio-dia local) e o servidor — a trava rígida de 3
 * meses fica no seletor de data do frontend; aqui é a rede de segurança.
 */
export function prazoDentroDoLimite(prazo: string | null | undefined): boolean {
  if (!prazo) {
    return true;
  }
  const p = new Date(prazo).getTime();
  if (Number.isNaN(p)) {
    return true; // formato já é validado por .datetime()
  }
  const agora = Date.now();
  const dia = 86_400_000;
  return p >= agora - 2 * dia && p <= prazoMaximo().getTime() + dia;
}

/**
 * Edição/revisão de um item-ação pelo time interno (FACR): texto do plano +
 * situação. `motivoRejeicao` acompanha a transição para `rejeitado`. A unidade
 * NÃO usa este schema (só anexa evidência); o texto/situação é do time CAINT.
 */
/** Uma ação do plano na borda: o quê, quem e até quando (FACR). */
export const planoAcaoItemSchema = z.object({
  texto: z.string().max(2000),
  responsavel: z.string().max(200),
  prazo: z
    .string()
    .datetime()
    .nullable()
    .refine(prazoDentroDoLimite, { message: `O prazo deve ser de no máximo ${MAX_MESES_PRAZO} meses.` }),
});

export const responderPlanoItemSchema = z
  .object({
    fato: z.string().max(2000).nullable().optional(),
    /** Lista de ações — o que a unidade de fato edita. Máx. 20 por item. */
    acoes: z.array(planoAcaoItemSchema).max(20).optional(),
    acao: z.string().max(2000).nullable().optional(),
    causa: z.string().max(2000).nullable().optional(),
    responsavel: z.string().max(200).nullable().optional(),
    prazo: z
      .string()
      .datetime()
      .nullable()
      .optional()
      .refine(prazoDentroDoLimite, { message: `O prazo deve ser de no máximo ${MAX_MESES_PRAZO} meses.` }),
    status: statusPlanoItemSchema.optional(),
    motivoRejeicao: z.string().max(2000).nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: "Nada para atualizar" })
  .refine((o) => o.status !== "rejeitado" || !!o.motivoRejeicao?.trim(), {
    message: "Informe o motivo da rejeição",
    path: ["motivoRejeicao"],
  });
export type ResponderPlanoItemDTO = z.infer<typeof responderPlanoItemSchema>;

/* ---------------- Ciclos (abertura pelo presidente) ---------------- */

export const abrirCicloSchema = z.object({
  unidadeId: z.string().min(1).max(60),
  dataInicio: z.string().datetime(),
  dataFim: z.string().datetime(),
  /** Escopo opcional na abertura; ausente ⇒ todos os setores (compat). */
  escopo: escopoDataSchema.optional(),
});
export type AbrirCicloDTO = z.infer<typeof abrirCicloSchema>;

export type StatusCiclo = "aberto" | "encerrado";

/** Edição de um ciclo (gestão): datas e/ou status — todos opcionais (patch). */
export const editarCicloSchema = z
  .object({
    dataInicio: z.string().datetime().optional(),
    dataFim: z.string().datetime().optional(),
    status: z.enum(["aberto", "encerrado"]).optional(),
  })
  .refine((d) => d.dataInicio !== undefined || d.dataFim !== undefined || d.status !== undefined, {
    message: "Informe ao menos um campo para editar",
  });
export type EditarCicloDTO = z.infer<typeof editarCicloSchema>;

/** Edição do escopo do ciclo pela gestão (independe de aberto/encerrado). */
export const salvarEscopoSchema = z.object({ escopo: escopoDataSchema });
export type SalvarEscopoDTO = z.infer<typeof salvarEscopoSchema>;

/** Ciclo como devolvido pela API (com a unidade resolvida). */
export interface CicloPublico {
  id: string;
  nome: string;
  unidadeId: string;
  unidadeNome: string;
  status: StatusCiclo;
  dataInicio: string;
  dataFim: string;
  avaliacaoId: string | null;
  abertoPor: string | null;
  /** Token do formulário público QACI/IMGR (monta a URL/QR no app). */
  tokenPublico: string | null;
  /** Escopo do ciclo (setores ativos); `null` ⇒ todos (compat). Doc 17b. */
  escopo: EscopoDataDTO | null;
}

/* ---------------- Matriz da Unidade (upload do roster esperado) ---------------- */

export const itemMatrizSchema = z.object({
  cargo: z.string().min(1).max(160),
  nivel: z.enum(["Estratégico", "Tático", "Operacional"]),
  area: z.string().min(1).max(80),
  perfil: z.enum(["Gestor", "Liderado"]).optional(),
  reportaPara: z.string().max(160).optional().default(""),
  quantitativo: z.number().int().min(0).max(1_000_000),
});
export const matrizUploadSchema = z.object({
  arquivoNome: z.string().max(200).optional(),
  itens: z.array(itemMatrizSchema).min(1).max(2000),
});
export type MatrizUploadDTO = z.infer<typeof matrizUploadSchema>;

/** Pedido de presigned URL para subir evidência (M5). */
export const evidenciaUploadSchema = z.object({
  avaliacaoId: z.string().min(1).max(60),
  itemId: z.string().min(1).max(20),
  momento: momentoSchema,
  contentType: z.string().regex(/^(image|application)\//),
  tamanhoBytes: z.number().int().positive().max(LIMITE_BYTES_EVIDENCIA),
});
export type EvidenciaUploadDTO = z.infer<typeof evidenciaUploadSchema>;

/** Confirmação após o upload direto ao bucket: persiste os metadados (M5).
 *  `chaveMiniatura` só vem quando o cliente conseguiu gerar e enviar a miniatura
 *  (ver `prepararEvidencia`); ausente, a galeria exibe a imagem cheia. */
export const evidenciaConfirmarSchema = evidenciaUploadSchema.extend({
  id: z.string().uuid(),
  chaveB2: z.string().min(1).max(300),
  chaveMiniatura: z.string().min(1).max(320).optional(),
});
export type EvidenciaConfirmarDTO = z.infer<typeof evidenciaConfirmarSchema>;

/** Exclusão de uma foto de evidência (hard-delete + trilha de auditoria). */
export const evidenciaRemoverSchema = z.object({
  id: z.string().uuid(),
  avaliacaoId: z.string().min(1).max(60),
});
export type EvidenciaRemoverDTO = z.infer<typeof evidenciaRemoverSchema>;

/* ---------------- Assinatura eletrônica (imagem) do usuário ---------------- */

/** Tipos de imagem aceitos para a assinatura (fundo transparente ideal → PNG). */
export const contentTypeAssinaturaSchema = z.enum(["image/png", "image/jpeg", "image/webp"]);
/** Limite: assinaturas são leves; 2 MB cobre PNG de alta resolução. */
export const LIMITE_BYTES_ASSINATURA = 2_000_000;

export const assinaturaUploadSchema = z.object({
  contentType: contentTypeAssinaturaSchema,
  tamanhoBytes: z.number().int().positive().max(LIMITE_BYTES_ASSINATURA),
});
export type AssinaturaUploadDTO = z.infer<typeof assinaturaUploadSchema>;

/** Confirmação após o PUT direto ao bucket: persiste referência + integridade. */
export const assinaturaConfirmarSchema = assinaturaUploadSchema.extend({
  id: z.string().uuid(),
  chaveB2: z.string().min(1).max(300),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type AssinaturaConfirmarDTO = z.infer<typeof assinaturaConfirmarSchema>;

/* ---------------- Evidências do Plano de Ação (bucket B2 próprio) ---------------- */

/**
 * Evidências que comprovam a execução de um item-ação. Storage é SEPARADO do
 * checklist (bucket/credenciais próprios). Só imagens e PDF; limite por arquivo
 * conforme boas práticas de upload (o servidor ainda reconfirma o tamanho real
 * via HEAD ao confirmar, pois o presign não assina o content-length).
 */
/**
 * O cliente NÃO oferece mais HEIC (ver `ACCEPT_IMAGEM`): com o `accept`
 * explícito o iOS já entrega JPEG, e o compressor ainda recodifica para WebP
 * antes de enviar. O servidor, porém, segue ACEITANDO `image/heic` de propósito
 * — o PWA atualiza sob confirmação do usuário (`registerType: "prompt"`), então
 * há aparelhos em campo com a versão antiga em cache; rejeitar aqui quebraria o
 * upload deles. Liberal no que recebe, conservador no que oferece.
 */
export const CONTENT_TYPES_PLANO_EVIDENCIA = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "application/pdf",
] as const;
/** Limite de 25 MB por arquivo (fotos de campo + documentos digitalizados). */
export const LIMITE_BYTES_PLANO_EVIDENCIA = 25_000_000;

const contentTypePlanoEvidenciaSchema = z.enum(CONTENT_TYPES_PLANO_EVIDENCIA);

/** Pedido de presigned URL para subir evidência de um item do Plano de Ação. */
export const planoEvidenciaUploadSchema = z.object({
  planoItemId: z.string().uuid(),
  contentType: contentTypePlanoEvidenciaSchema,
  tamanhoBytes: z.number().int().positive().max(LIMITE_BYTES_PLANO_EVIDENCIA),
});
export type PlanoEvidenciaUploadDTO = z.infer<typeof planoEvidenciaUploadSchema>;

/** Confirmação após o upload direto ao bucket do plano: persiste os metadados. */
export const planoEvidenciaConfirmarSchema = planoEvidenciaUploadSchema.extend({
  id: z.string().uuid(),
  chaveB2: z.string().min(1).max(300),
  chaveMiniatura: z.string().min(1).max(320).optional(),
});
export type PlanoEvidenciaConfirmarDTO = z.infer<typeof planoEvidenciaConfirmarSchema>;

/** Exclusão de uma evidência do plano (hard-delete + trilha de auditoria). */
export const planoEvidenciaRemoverSchema = z.object({
  id: z.string().uuid(),
  planoItemId: z.string().uuid(),
});
export type PlanoEvidenciaRemoverDTO = z.infer<typeof planoEvidenciaRemoverSchema>;

/* ---------------- Migração de catálogo (Workbench) ---------------- */

const codigoItemSchema = z.string().regex(/^[A-Z]{2,4}\.\d{2}$/);
/** Só campos descritivos migram — peso/eixo/p/i são rejeitados na borda. */
export const camposTextoSchema = z
  .record(z.string(), z.string().max(4000))
  .refine((o) => Object.keys(o).every((k) => ["pergunta", "processo", "norma", "evidencia"].includes(k)), {
    message: "campos permitidos: pergunta, processo, norma, evidencia",
  });

export const propostaItemSchema = z.object({
  setor: z.string().min(2).max(8),
  codigoOrigem: codigoItemSchema,
  codigoDestino: codigoItemSchema.nullable().default(null),
  tipoProposto: z.enum(["refinamento", "substituicao", "divergente"]),
  similaridade: z.number().min(0).max(1).nullable().default(null),
  temAvaliacao: z.boolean().default(false),
  perguntaAtual: z.string().max(2000).nullable().default(null),
  perguntaNova: z.string().max(2000).nullable().default(null),
  camposTexto: camposTextoSchema.nullable().default(null),
});

export const ingerirPropostaSchema = z.object({
  titulo: z.string().min(1).max(160),
  origem: z.string().max(40).default("sync-canone"),
  versaoBase: z.string().min(1).max(20),
  versaoAlvo: z.string().min(1).max(20),
  itens: z.array(propostaItemSchema).max(2000),
});
export type IngerirPropostaDTO = z.infer<typeof ingerirPropostaSchema>;

export const decisaoMigracaoSchema = z.object({
  decisao: z.enum(["manter", "migrar_texto", "migrar_recode", "rejeitar"]),
  codigoDestino: codigoItemSchema.nullable().optional(),
});
export type DecisaoMigracaoDTO = z.infer<typeof decisaoMigracaoSchema>;

/* ---------------- Relatório Preliminar de Avaliação de Maturidade ---------------- */

/**
 * DTO totalmente computado do relatório institucional (documento formal da
 * CAINT). O backend monta-o com a lógica pura de `@caint/relatorio`/`@caint/engine`
 * e valida na saída com este schema (belt-and-suspenders); o frontend consome o
 * tipo inferido. Momento sempre `mi` (avaliação inicial). Campos anuláveis são
 * dados ainda não preenchidos (nº oficial, CNES, parecer AUDIN etc.).
 */
export const relatorioSchema = z.object({
  meta: z.object({
    /** Número oficial sequencial por ano (NNNN/AAAA), atribuído na 1ª emissão. */
    numero: z.string().nullable(),
    /** Ordem do ciclo na unidade (1º, 2º, …), derivada por data de início. */
    cicloNumero: z.number(),
    /** 1º ciclo ⇒ documento preliminar; 2º+ ⇒ documento final. */
    tipo: z.enum(["preliminar", "final"]),
    natureza: z.string(),
    estado: z.string(),
    emitidoEm: z.string(),
    portaria: z.string(),
  }),
  identificacao: z.object({
    unidadeNome: z.string(),
    municipio: z.string(),
    uf: z.string(),
    cnes: z.string().nullable(),
    mantenedora: z.string(),
    comissao: z.string(),
    escopoSetores: z.number(),
    escopoItensRespondidos: z.number(),
    inicioCiclo: z.string().nullable(),
  }),
  subindices: z.array(
    z.object({
      eixo: z.enum(["IMGO", "IMQA", "IMGR"]),
      nome: z.string(),
      score: z.number().nullable(),
      nItens: z.number(),
    }),
  ),
  imgq: z.object({
    bruto: z.number(),
    efetivo: z.number(),
    seloChave: z.string(),
    seloRotulo: z.string(),
    seloNivel: z.string(),
    travado: z.boolean(),
    core0: z.number(),
    core1: z.number(),
  }),
  setores: z.array(
    z.object({
      code: z.string(),
      nome: z.string(),
      score: z.number().nullable(),
      nItens: z.number(),
      nc: z.number(),
      coreCriticos: z.number(),
      noPlano: z.number(),
    }),
  ),
  ona: z.object({
    nc: z.number(),
    pc: z.number(),
    c: z.number(),
    cs: z.number(),
    na: z.number(),
    totalAvaliado: z.number(),
  }),
  riscos: z.object({
    rp: z.number(),
    rm: z.number(),
    ra: z.number(),
    rc: z.number(),
    plano15: z.number(),
    plano60: z.number(),
  }),
  plano: z.object({
    total: z.number(),
    itens: z.array(
      z.object({
        code: z.string(),
        setor: z.string(),
        /** Nome do setor (05A → "Governança e Gestão") — legenda do código. */
        setorNome: z.string(),
        /** Enunciado do item do catálogo — dá significado ao código. */
        pergunta: z.string(),
        /** Nota que originou o item — é o FATO do FACR (doc: 24/ago/2026). */
        maturidadeGatilho: z.string().nullable(),
        fato: z.string().nullable(),
        causa: z.string().nullable(),
        acao: z.string().nullable(),
        responsavel: z.string().nullable(),
        prazo: z.string().nullable(),
        status: z.string(),
        obrigatorio: z.boolean(),
      }),
    ),
  }),
  audin: z
    .object({
      parecer: z.string().nullable(),
      taxaCorroboracao: z.number().nullable(),
      ncAltaCriticidade: z.number().nullable(),
      temDados: z.boolean(),
    })
    .nullable(),
  /**
   * Percepção interna — questionário público do Manual IMGR (31 questões, 6
   * componentes, escala 0–3) respondido pela equipe da unidade no ciclo. NÃO
   * entra no IMGQ: é autoavaliação, não evidência auditada. Vive aqui só para o
   * CONTRASTE com o subíndice IMGR do checklist (`gapPontos`) — a distância
   * entre o que a equipe percebe e o que a auditoria constata é, ela própria, um
   * achado. `null` quando o ciclo não teve respostas.
   */
  percepcao: z
    .object({
      /** IMGR do questionário, 0–100 (escala PRÓPRIA, não é o subíndice 0–1). */
      imgr: z.number().nullable(),
      faixa: z.string().nullable(),
      nRespondentes: z.number(),
      nGestores: z.number(),
      nLiderados: z.number(),
      componentes: z.array(z.object({ id: z.string(), nome: z.string(), pct: z.number().nullable() })),
      /** percepção − IMGR auditado, em pontos percentuais. null se faltar um dos dois. */
      gapPontos: z.number().nullable(),
    })
    .nullable(),
  consideracoes: z.object({
    findings: z.array(
      z.object({
        dimensao: z.string(),
        severidade: z.string(),
        titulo: z.string(),
        texto: z.string(),
      }),
    ),
    planoTotal: z.number(),
    riscoCritico: z.number(),
    simuladorPassos: z.number(),
    simuladorAtingiu: z.boolean(),
    imgqInicial: z.number(),
    imgqFinal: z.number(),
  }),
  fundamentacao: z.array(z.string()),
  /** Avaliadores que preencheram o checklist DESTA unidade (autores das respostas
   *  + trilha de auditoria). Se vazio na origem, cai no quadro de avaliadores da
   *  comissão (ver `avaliadoresPresumidos`). */
  avaliadores: z.array(
    z.object({
      nome: z.string(),
      papel: z.string(),
      eixo: z.string().nullable(),
      /** URL presigned (curta) da imagem da assinatura; null se não houver. */
      assinaturaUrl: z.string().nullable(),
    }),
  ),
  /** true = a lista de avaliadores é o quadro da comissão (o sistema não sabia
   *  quem avaliou); false = são os avaliadores REAIS desta avaliação. */
  avaliadoresPresumidos: z.boolean(),
  /** Supervisores AUDIN que validaram esta avaliação. */
  supervisores: z.array(z.object({ nome: z.string(), assinaturaUrl: z.string().nullable() })),
  signatarios: z.array(z.object({ nome: z.string(), cargo: z.string(), matricula: z.string() })),
});
export type RelatorioDTO = z.infer<typeof relatorioSchema>;
